import { useEffect, useState } from 'react';
import { Badge } from './Badge';
import { Button, Confetti, Modal } from '../ui';
import { formatNumber } from '../../lib/format';

/**
 * The tier-up celebration.
 *
 * Scaled to the achievement, deliberately. A Bronze tier gets the badge, its
 * name and a button; the top tier gets confetti, rays and a heavier line of
 * copy. If every rung got the full treatment then the top rung — the thing a
 * player spent a year on — would look exactly like the one they passed in
 * their first hour, and the whole ladder would flatten.
 *
 * Queued rather than stacked: several tiers can fall at once (a big hatch can
 * cross three), and three modals fighting over the screen is not a reward.
 */

export interface BadgeAward {
  badgeKey: string;
  name: string;
  description: string;
  tier: number;
  tierName: string;
  isTopTier: boolean;
  shape: string;
  motif: string;
  colorBase: string;
  colorAccent: string;
  score: number;
  rewardCoins: number;
  rewardTokens: number;
  rewardTitle: string | null;
  rewardFrame: string | null;
}

/** The award payload is not a full Badge; this adapts it for the artwork. */
const toVisual = (award: BadgeAward) => ({
  key: award.badgeKey,
  name: award.name,
  shape: award.shape,
  motif: award.motif,
  colorBase: award.colorBase,
  colorAccent: award.colorAccent,
  tier: award.tier,
  tierName: award.tierName,
  isTopTier: award.isTopTier,
});

export function BadgeFanfare({ award, onClose }: { award: BadgeAward; onClose: () => void }) {
  // A beat before the button appears, so nobody dismisses the celebration with
  // a click they had already started making somewhere else on the page.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setReady(true), 700);
    return () => window.clearTimeout(timer);
  }, []);

  const rewards = [
    award.rewardCoins > 0 ? `${formatNumber(award.rewardCoins)} PokéCoins` : null,
    award.rewardTokens > 0 ? `${formatNumber(award.rewardTokens)} Friendship Tokens` : null,
    award.rewardTitle ? `Title: ${award.rewardTitle}` : null,
    award.rewardFrame ? 'A new profile frame' : null,
  ].filter(Boolean) as string[];

  return (
    <Modal onClose={onClose} labelledBy="fanfare-title">
      {award.isTopTier && <Confetti count={28} />}
      <div
        className={`badge-fanfare ${award.isTopTier ? 'badge-fanfare--master' : ''}`}
        style={{ position: 'relative' }}
      >
        <div className="badge-fanfare__tier" style={{ color: 'var(--gold-dark)' }}>
          {award.isTopTier ? '✦ Mastered ✦' : `${award.tierName} tier`}
        </div>

        <div className="badge-fanfare__badge">
          <Badge badge={toVisual(award)} size={award.isTopTier ? 190 : 150} />
        </div>

        <h2 id="fanfare-title" className="panel__title" style={{ margin: 0 }}>
          {award.name}
        </h2>

        <p className="tiny muted" style={{ margin: 0, maxWidth: 320 }}>
          {award.isTopTier
            ? `You have taken this as far as it goes. ${award.description}`
            : award.description}
        </p>

        <div className="chip">+{award.score} Badge Score</div>

        {rewards.length > 0 && (
          <div className="pxwell tiny" style={{ padding: 'var(--gap-sm)', maxWidth: 320 }}>
            {rewards.join(' · ')}
          </div>
        )}

        <Button variant={award.isTopTier ? 'gold' : 'primary'} onClick={onClose} disabled={!ready}>
          {award.isTopTier ? 'Magnificent' : 'Nice'}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Renders the queue one at a time.
 *
 * Sorted so the biggest one lands last: crossing Bronze, Silver and Gold in a
 * single hatch should build, not anticlimax.
 */
export function BadgeFanfareQueue({
  awards,
  onDone,
}: {
  awards: BadgeAward[];
  onDone: () => void;
}) {
  const [index, setIndex] = useState(0);
  const ordered = [...awards].sort((a, b) => a.tier - b.tier);
  const current = ordered[index];

  if (!current) return null;

  return (
    <BadgeFanfare
      key={current.badgeKey + current.tier}
      award={current}
      onClose={() => {
        if (index + 1 >= ordered.length) onDone();
        else setIndex(index + 1);
      }}
    />
  );
}

/** Convenience: the shape most mutation responses hand back. */
export type WithBadges = { badges?: BadgeAward[] };
